import { Header } from '@hub/app/shell/Header';
import { SkipToMainLink } from '@hub/app/shell/SkipToMainLink';
import { m } from '@hub/messages';
import { useFeatureFlag } from '@platform/feature-flags';
import { lazy, Suspense } from 'react';
import type { RouterContext } from '..';
import { createRootRouteWithContext, notFound, Outlet, useLocation } from '..';
import { isHubMountAllowed } from '../basepath';
import { DefaultNotFoundComponent } from '../components/DefaultNotFoundComponent';
import { useLegacyRedirect } from '../redirects';
import { RootShell } from './RootShell';

const LazyDevtools = lazy(() => import('../../app/Devtools'));
const LazyTrackingInspector = lazy(() => import('../../tracking/inspector/TrackingInspector'));

function RootComponent() {
  const devtools = useFeatureFlag('platform-devtools');
  const trackingInspector = useFeatureFlag('platform-tracking-inspector');

  return (
    <>
      <Outlet />
      {devtools ? (
        <Suspense>
          <LazyDevtools />
        </Suspense>
      ) : null}
      {trackingInspector ? (
        <Suspense>
          <LazyTrackingInspector />
        </Suspense>
      ) : null}
    </>
  );
}

// Minimal shell without Footer, navbar, or max-width constraints so the
// 404 page can render full-bleed stripe background (WWP-222).
function NotFoundShell() {
  const { pathname } = useLocation();
  const isRedirecting = useLegacyRedirect(pathname);

  if (isRedirecting) {
    return null;
  }

  return (
    <div className="flex min-h-screen flex-col bg-header">
      <SkipToMainLink targetId="hub-main" />
      <Header />
      <main id="hub-main" tabIndex={-1} className="flex flex-1 flex-col overflow-x-clip">
        <DefaultNotFoundComponent />
      </main>
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Gate the /_hub escape-hatch mount behind a flag so prod can kill it without an nginx change.
  beforeLoad: ({ context }) => {
    if (!isHubMountAllowed(context.featureFlags)) {
      throw notFound();
    }
  },
  component: RootComponent,
  notFoundComponent: NotFoundShell,
  shellComponent: RootShell,
  head: () => ({
    meta: [{ title: m.commons_meta_title() }, { name: 'description', content: m.commons_meta_description() }],
  }),
});
